import moment from 'moment';

// Day meter for each technician on the dispatch timeline: 8 segments, one per
// hour of an 8-hour work day, filled by the hours booked on the shown day.

export const WORKDAY_HOURS = 8;

// Bands, in order. A total above a band's `upTo` falls into the next one.
const BANDS = [
  { key: 'free', upTo: 0, label: 'free' },
  { key: 'light', upTo: 3, label: 'has time' },
  { key: 'busy', upTo: 6, label: 'near capacity' },
  { key: 'full', upTo: 8, label: 'at capacity' },
  { key: 'over', upTo: Infinity, label: 'over capacity' },
];

export function workloadBand(hours) {
  return BANDS.find((band) => hours <= band.upTo);
}

const resourceIdsOf = (event) => {
  if (event.resourceId != null) return [String(event.resourceId)];
  if (Array.isArray(event.resourceIds)) return event.resourceIds.map(String);
  return [];
};

// Hours booked per technician within [dayStart, dayEnd). Vacations and
// background blocks don't count. A card just dropped from a side panel is
// skipped once the saved copy of the same job has arrived.
export function bookedHoursByTechnician(events, dayStart, dayEnd) {
  const start = moment(dayStart);
  const end = moment(dayEnd);
  const savedIds = new Set(events.filter((e) => !e.__dropped).map((e) => e.id));
  const totals = {};

  events.forEach((event) => {
    if (event.isVacation || event.rendering === 'background') return;
    if (event.__dropped && savedIds.has(event.id)) return;
    const eventStart = moment(event.start);
    // endForDisplay is the real end (timeline `end` is padded for layout).
    const realEnd = event.endForDisplay || event.end;
    const eventEnd = realEnd ? moment(realEnd) : eventStart.clone();
    const from = moment.max(eventStart, start);
    const to = moment.min(eventEnd, end);
    const hours = Math.max(0, to.diff(from, 'minutes')) / 60;
    if (!hours) return;
    resourceIdsOf(event).forEach((id) => {
      if (id === '0') return; // the "Unassigned Jobs" row
      totals[id] = (totals[id] || 0) + hours;
    });
  });

  return totals;
}

const formatHours = (hours) => {
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  if (!whole) return `${minutes}m`;
  return minutes ? `${whole}h ${minutes}m` : `${whole}h`;
};

const shortHours = (hours) => `${Math.round(hours * 10) / 10}h`;

// Markup for one technician's meter.
export function workloadMeterHtml(hours) {
  const band = workloadBand(hours);
  const segments = Array.from({ length: WORKDAY_HOURS }, (_, i) => {
    const fill = Math.max(0, Math.min(1, hours - i));
    return `<span class="tech-load__seg"><span class="tech-load__fill" style="width:${Math.round(fill * 100)}%"></span></span>`;
  }).join('');
  const over = hours - WORKDAY_HOURS;
  const value = over > 0 ? `+${shortHours(over)}` : shortHours(hours);
  const description = hours
    ? `${formatHours(hours)} booked of ${WORKDAY_HOURS}h · ${band.label}`
    : `Nothing booked · ${band.label}`;
  return `<span class="tech-load tech-load--${band.key}" role="img" aria-label="${description}" title="${description}">`
    + `<span class="tech-load__bar">${segments}</span>`
    + `<span class="tech-load__value">${value}</span>`
    + `</span>`;
}
